import { useEffect, useState } from 'react';
import { useI18n } from '../i18n/I18nContext';
import { Button, Icon } from './components';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);

export const PwaInstallButton = ({ compact = false }: { compact?: boolean }) => {
  const { t } = useI18n();
  const [promptEvent, setPromptEvent] = useState<InstallPromptEvent | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const receivePrompt = (event: Event) => {
      event.preventDefault();
      setPromptEvent(event as InstallPromptEvent);
    };
    const receiveInstalled = () => {
      setInstalled(true);
      setPromptEvent(null);
      setStatus(t('install.installed'));
    };
    window.addEventListener('beforeinstallprompt', receivePrompt);
    window.addEventListener('appinstalled', receiveInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', receivePrompt);
      window.removeEventListener('appinstalled', receiveInstalled);
    };
  }, [t]);

  const install = async () => {
    if (installed || isStandalone()) {
      setInstalled(true);
      setStatus(t('install.alreadyInstalled'));
      return;
    }
    if (promptEvent != null) {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      setPromptEvent(null);
      setStatus(
        choice.outcome === 'accepted'
          ? t('install.started')
          : t('install.cancelled'),
      );
      return;
    }

    const isAppleMobile = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setStatus(
      isAppleMobile
        ? t('install.appleHelp')
        : t('install.browserHelp'),
    );
  };

  return (
    <div className={`install-cta${compact ? ' install-cta--compact' : ''}`}>
      <Button
        variant={compact ? 'secondary' : 'primary'}
        onClick={() => void install()}
        icon={<Icon name="download" />}
      >
        {installed ? t('install.doneCta') : t('install.cta')}
      </Button>
      {status != null ? <p className="install-cta__status" role="status">{status}</p> : null}
    </div>
  );
};
